import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, ExternalLink, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const isPdfName = (name = '') => /\.pdf$/i.test(name);

/** Authenticated image fetched as a blob (PDF pages, proxied originals). */
function BlobImage({ api, url, alt, className, testId, onFail }) {
  const [src, setSrc] = useState(null);
  const onFailRef = useRef(onFail);
  onFailRef.current = onFail;
  useEffect(() => {
    let alive = true; let obj = null;
    setSrc(null);
    api.get(url, { responseType: 'blob', timeout: 120000 })
      .then(r => { obj = URL.createObjectURL(r.data); if (alive) setSrc(obj); })
      .catch(() => { if (alive) onFailRef.current?.(); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [api, url]);
  if (!src) return <div className={`flex items-center justify-center min-h-[200px] ${className || ''}`} data-testid={`${testId}-loading`}><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>;
  return <img src={src} alt={alt} className={className} draggable={false} data-testid={testId} />;
}

/** PDF shown as a scrollable stack of page images rendered server-side — works on phones, never downloads. */
function PdfPages({ api, base, file }) {
  const [count, setCount] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setCount(null); setFailed(false);
    api.get(`${base}/files/${file.id}/pdf-pages`).then(r => { if (alive) setCount(r.data.page_count || 0); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [api, base, file.id]);
  if (failed) return <p className="text-sm text-gray-400" data-testid="file-lightbox-error">Couldn't load this PDF — use Download instead.</p>;
  if (count === null) return <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" data-testid="file-lightbox-loading" />;
  return (
    <div className="w-full h-full overflow-y-auto px-2 py-3 space-y-3" data-testid="file-lightbox-pdf">
      {Array.from({ length: count }, (_, i) => (
        <BlobImage key={`${file.id}-${i}`} api={api} url={`${base}/files/${file.id}/pdf-pages/${i}?width=1400`} alt={`${file.name} page ${i + 1}`} className="w-full max-w-4xl mx-auto rounded shadow-lg bg-white" testId={`file-lightbox-pdf-page-${i}`} />
      ))}
      <p className="text-center text-[11px] text-gray-500">{count} {count === 1 ? 'page' : 'pages'}</p>
    </div>
  );
}

/** Full-screen viewer for a folder's images/PDFs — arrows, keyboard, swipe, thumbnail strip. */
export function FileLightbox({ entityType, entityId, files, index, onIndexChange, onClose, onDownload }) {
  const { api } = useAuth();
  const file = files[index];
  const isPdf = isPdfName(file?.name);
  const [failed, setFailed] = useState(false);
  const touchX = useRef(null);
  const stripRef = useRef(null);
  const base = `/${entityType}s/${entityId}`;

  const go = (delta) => { if (files.length > 1) onIndexChange((index + delta + files.length) % files.length); };

  useEffect(() => { setFailed(false); }, [file]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'ArrowRight') go(1); else if (e.key === 'ArrowLeft') go(-1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    [files[index + 1], files[index - 1]].forEach(f => { if (f?.preview_url) { const im = new Image(); im.src = f.preview_url; } });
    stripRef.current?.querySelector(`[data-idx="${index}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [files, index]);

  if (!file) return null;
  const imgSrc = isPdf ? null : (file.preview_url || file.thumbnail_large_url);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] p-0 gap-0 bg-[#0B0E13] border-white/10 flex flex-col overflow-hidden [&>button]:hidden" data-testid="file-lightbox">
        <DialogTitle className="sr-only">{file.name}</DialogTitle>
        <DialogDescription className="sr-only">Browse the images and PDFs in this folder.</DialogDescription>
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate" data-testid="file-lightbox-name">{file.name}</div>
            <div className="text-[11px] text-gray-500" data-testid="file-lightbox-counter">{index + 1} of {files.length}</div>
          </div>
          <button type="button" onClick={() => onDownload(file)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-white/10 text-white hover:bg-white/15" data-testid="file-lightbox-download"><Download className="w-3.5 h-3.5" /> Download</button>
          {file.web_url && <a href={file.web_url} target="_blank" rel="noreferrer" className="p-1.5 rounded-md text-gray-400 hover:text-white" title="Open in SharePoint" data-testid="file-lightbox-sharepoint"><ExternalLink className="w-4 h-4" /></a>}
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-gray-400 hover:text-white" data-testid="file-lightbox-close"><X className="w-5 h-5" /></button>
        </div>

        <div
          className="relative flex-1 min-h-0 flex items-center justify-center select-none"
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touchX.current ?? e.changedTouches[0].clientX); if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touchX.current = null; }}
        >
          {failed && <p className="text-sm text-gray-400" data-testid="file-lightbox-error">Couldn't load this file — use Download instead.</p>}
          {!failed && isPdf && <PdfPages key={file.id} api={api} base={base} file={file} />}
          {!failed && !isPdf && imgSrc && (
            <img key={file.id} src={imgSrc} alt={file.name} onError={() => setFailed(true)} className="max-w-full max-h-full object-contain" draggable={false} data-testid="file-lightbox-image" />
          )}
          {!failed && !isPdf && !imgSrc && (
            <BlobImage key={file.id} api={api} url={`${base}/files/${file.id}/content`} alt={file.name} className="max-w-full max-h-full object-contain" testId="file-lightbox-image" onFail={() => setFailed(true)} />
          )}
          {files.length > 1 && (
            <>
              <button type="button" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 text-white hover:bg-black/80" aria-label="Previous" data-testid="file-lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
              <button type="button" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 text-white hover:bg-black/80" aria-label="Next" data-testid="file-lightbox-next"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
        </div>

        {files.length > 1 && (
          <div ref={stripRef} className="flex gap-1.5 px-3 py-2 overflow-x-auto border-t border-white/10 bg-black/40" data-testid="file-lightbox-strip">
            {files.map((f, i) => (
              <button key={f.id} type="button" data-idx={i} onClick={() => onIndexChange(i)} className={`w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border-2 ${i === index ? 'border-[#F5A623]' : 'border-transparent opacity-60 hover:opacity-100'}`} title={f.name} data-testid={`file-lightbox-thumb-${i}`}>
                {f.thumbnail_url ? <img src={f.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-white/10 text-[9px] text-gray-300 flex items-center justify-center">{isPdfName(f.name) ? 'PDF' : 'file'}</div>}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
